import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChangeLangService } from './change-lang';
import { LiveQueryService } from './Live-Query.service';
import { SessionService } from './session';
import { StateService } from './state';

/**
 * Parse error codes that mean the session token is no longer usable:
 * 206 SESSION_MISSING, 209 INVALID_SESSION_TOKEN (also returned when a
 * session has expired or was revoked from another device).
 */
const INVALID_SESSION_CODES = new Set([206, 209]);

/** Backend wording for an unusable session, for responses that carry no code. */
const INVALID_SESSION_MESSAGE = /invalid session|session token|session (has )?expired/i;

/**
 * Marks that the redirect to /login was caused by an expired session, so the
 * login screen can explain why. It has to survive the navigation (and a full
 * reload, since expiry is also detected during app initialization, before the
 * toast outlet exists), hence localStorage rather than a signal.
 */
const EXPIRED_NOTICE_KEY = 'sessionExpired';

const EXPIRED_MESSAGE: Record<'en' | 'ar', string> = {
  en: 'Your session has expired. Please log in again.',
  ar: 'انتهت صلاحية جلستك. يرجى تسجيل الدخول مرة أخرى.',
};

/**
 * Owns what happens when the backend stops accepting the stored session token.
 *
 * The backend issues opaque Parse session tokens and exposes no refresh
 * endpoint, so there is nothing to renew silently: the only correct response is
 * to tear the session down and send the user back to the login screen. Until
 * that happens the service also flags the session as expired so the interceptor
 * can stop firing further requests with the dead token.
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryService {
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private stateService = inject(StateService);
  private dialogService = inject(DialogService);
  private liveQueryService = inject(LiveQueryService);
  private langService = inject(ChangeLangService);

  private readonly expiredSignal = signal(false);

  /** True from the moment expiry is detected until the login screen is reached. */
  readonly isExpired = this.expiredSignal.asReadonly();

  /** The "session expired" message in the active language. */
  readonly expiredMessage = computed<string>(() => {
    const lang = (this.langService.currentLang() as 'en' | 'ar') || 'en';
    return EXPIRED_MESSAGE[lang] ?? EXPIRED_MESSAGE.en;
  });

  /**
   * True when this failure means the session token was rejected — as opposed to
   * a deactivated account (403 + license payload) or any other backend error.
   */
  isSessionExpiredError(error: HttpErrorResponse): boolean {
    if (error.status === 401) return true;
    // Parse also reports a rejected token as 400/403 with the code in the body.
    if (error.status !== 400 && error.status !== 403) return false;

    const body = error.error;
    if (!body || typeof body !== 'object') return false;
    if (INVALID_SESSION_CODES.has(Number(body.code))) return true;

    const raw = body.error ?? body.message;
    const text =
      typeof raw === 'string'
        ? raw
        : raw && typeof raw === 'object'
        ? `${raw.en ?? ''} ${raw.ar ?? ''}`
        : '';
    return INVALID_SESSION_MESSAGE.test(text);
  }

  /**
   * Tear down the expired session and redirect to login. Idempotent: a screen
   * that fired several requests in parallel gets several 401s back at once, and
   * only the first one may clear state and navigate.
   */
  handleExpiredSession(): void {
    if (this.expiredSignal()) return;
    this.expiredSignal.set(true);

    localStorage.setItem(EXPIRED_NOTICE_KEY, '1');
    this.clearClientSession();
    this.closeOpenDialogs();

    const returnUrl = this.currentUrl();
    this.router.navigate(['/login'], {
      // Keep the destination so the user resumes where they were after logging
      // back in; anything outside the app shell isn't worth returning to.
      queryParams: returnUrl.startsWith('/main') ? { returnUrl } : {},
      // replaceUrl so Back can't return to a screen the session no longer covers.
      replaceUrl: true,
    });
  }

  /**
   * The screen the user is on. Expiry is also detected during app
   * initialization, before the first navigation resolves — `router.url` is
   * still '/' then, so fall back to the hash the browser was opened with.
   */
  private currentUrl(): string {
    const routed = this.router.url;
    if (routed && routed !== '/') return routed;
    const hash = window.location.hash.replace(/^#/, '');
    return hash.split('?')[0] || '/';
  }

  /**
   * True when the current visit to the login screen was caused by an expiry.
   * Consumes the notice so it is shown exactly once.
   */
  consumeExpiredNotice(): boolean {
    const pending = localStorage.getItem(EXPIRED_NOTICE_KEY) !== null;
    localStorage.removeItem(EXPIRED_NOTICE_KEY);
    return pending;
  }

  /**
   * Lift the block on outgoing requests. Called from the screens that can
   * legitimately run without a session (login, self-registration), so their own
   * API calls are not caught by the guard left behind by the expired session.
   */
  reset(): void {
    this.expiredSignal.set(false);
    localStorage.removeItem(EXPIRED_NOTICE_KEY);
  }

  /** Drop every trace of the session held by the client. */
  private clearClientSession(): void {
    this.sessionService.clearSession();
    this.stateService.userRole.set('');
    localStorage.removeItem('username');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    // The LiveQuery socket authenticates with the same token and would keep
    // reconnecting with it otherwise.
    this.liveQueryService.close();
  }

  /**
   * Dismiss anything modal that was open when the session died — a dialog left
   * mounted would otherwise sit on top of the login screen.
   */
  private closeOpenDialogs(): void {
    const refMap: Map<DynamicDialogRef, unknown> | undefined = (
      this.dialogService as any
    )?.dialogComponentRefMap;
    if (!refMap) return;

    for (const ref of Array.from(refMap.keys())) {
      try {
        ref.close();
      } catch {
        /* already destroyed */
      }
    }
  }
}
